import React, { useContext, useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Box,
  Typography,
  List,
  Divider,
  Popover,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  ListItem,
  ListItemText,
  ListItemButton,
  Badge,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  Collapse,
  keyframes,
  Grid,
  ClickAwayListener,
} from "@mui/material";

import { HiMenuAlt3 } from "react-icons/hi";
import { CiBrightnessUp, CiDark } from "react-icons/ci";
import { BsBell } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MdExpandLess, MdExpandMore, MdClose } from "react-icons/md";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { ColorModeContext } from "../../ThemeContext";
import { NewsStore } from "../../store/newsStore";
import { BASE_URL } from "../../api/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const noImage = "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";

const marquee = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0%); }
`;

export default function Header() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [notifyAnchor, setNotifyAnchor] = useState(null);
  const { news, notifications, fetchNotifications, hasMoreNotifications, notificationLoading } = NewsStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestion, setSuggestion] = useState([]);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileInputRef = useRef(null);
  const [notificationPage, setNotificationPage] = useState(1);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [readNotifications, setReadNotifications] = useState(false);
  const [lastSeenId, setLastSeenId] = useState(() => {
    return parseInt(localStorage.getItem("lastSeenId") || "0", 10);
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState({});

  const handleMobileMenuToggle = (slug) => {
    setMobileMenuOpen((prev) => ({ ...prev, [slug]: !prev[slug] }));
  };

  // HOVER MENU STATE
  const [hoverMenuAnchor, setHoverMenuAnchor] = useState(null);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const closeMenuTimer = useRef(null);

  const handleHoverOpen = (event, item) => {
    if (closeMenuTimer.current) {
      clearTimeout(closeMenuTimer.current);
      closeMenuTimer.current = null;
    }
    if (item.cities) {
      setHoverMenuAnchor(event.currentTarget);
      setHoveredNavItem(item);
    }
  };

  const handleHoverClose = () => {
    closeMenuTimer.current = setTimeout(() => {
      setHoverMenuAnchor(null);
      setHoveredNavItem(null);
    }, 200);
  };

  const handleMenuEnter = () => {
    if (closeMenuTimer.current) {
      clearTimeout(closeMenuTimer.current);
      closeMenuTimer.current = null;
    }
  };

  const handleCityClick = (city) => {
    if (hoveredNavItem) {
      navigate(`/category/${hoveredNavItem.slug}?city=${city}`);
      setHoverMenuAnchor(null);
      setHoveredNavItem(null);
    }
  };

  const handleOpenNotify = (event) => {
    setNotifyAnchor(event.currentTarget);
    setReadNotifications(true);
    // Fetch notifications only when the bell icon is clicked
    if (notifications.length === 0) {
      fetchNotifications(1, 10);
    }
  };

  const handleCloseNotify = () => {
    setNotifyAnchor(null);
    if (notifications?.length > 0) {
      const latestId = notifications[0].id;
      if (latestId > lastSeenId) {
        localStorage.setItem("lastSeenId", latestId);
        setLastSeenId(latestId);
      }
    }
  };

  const openNotify = Boolean(notifyAnchor);

  // POPUP STATES
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenPopup = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopup = () => {
    setAnchorEl(null);
  };

  const openPopup = Boolean(anchorEl);

  const navItems = [
    {
      label: "छत्तीसगढ़",
      slug: "chhattisgarh",
      cities: ["रायपुर", "बिलासपुर", "दुर्ग", "भिलाई", "कोरबा", "रायगढ़", "जगदलपुर", "अंबिकापुर"],
    },
    {
      label: "मध्य प्रदेश",
      slug: "madhya-pradesh",
      cities: ["भोपाल", "इंदौर", "ग्वालियर", "जबलपुर", "उज्जैन", "सागर", "सतना", "रीवा"],
    },
    { label: "मनोरंजन", slug: "entertainment" },
    { label: "खेल", slug: "sports" },
    { label: "धर्म", slug: "religion" },
    { label: "वायरल वीडियो", slug: "viral-videos" },
    { label: "विदेश", slug: "world" },
  ];

 // Removed redundant fetchNews call. Home.js will be responsible for fetching news.
 // useEffect(() => {
 //  if(!news || news.length === 0){
 //    fetchNews(1, 20);
 //  }
 // },[]);

 // Removed fetchNotifications from here to prevent it from running on every page load.
 // It will now be called when the user clicks the notification bell.
 // useEffect(() => {
 //  fetchNotifications(1, 10);
 // }, []);

 const handleNotificationScroll = (e) => {
  const { scrollTop, clientHeight, scrollHeight } = e.target;
  if (scrollHeight - scrollTop <= clientHeight + 1) {
    if (hasMoreNotifications && !notificationLoading) {
      const nextPage = notificationPage + 1;
      setNotificationPage(nextPage);
      fetchNotifications(nextPage, 10);
    }
  }
 };

 useEffect(() => {
  const timer = setTimeout(async () => {
    if (searchQuery.length >= 1) {
      try {
        const response = await fetch(`/api/all-news?search=${searchQuery}&page=1&limit=5`);
        const data = await response.json();
        setSuggestion(data?.data || []);
      }
      catch (error) {
        console.log(error);
      }
    }
    else {
      setSuggestion([]);
    }
  }, 300);
  return () => clearTimeout(timer);
 }, [searchQuery]);

 useEffect(() => {
   const timer = setInterval(() => {
     setCurrentTime(Date.now());
   }, 60000);
   return () => clearInterval(timer);
 }, []);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "background.default",
          color: "text.primary",
          boxShadow: 1,
          zIndex: 1100,

        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: {
              xs: "space-between",
              sm: "space-between",
              lg: "space-around",
            },
            py: 1,
          }}
        >
          {/* LOGO */}
          <Box
            sx={{ display: "flex", alignItems: "center", maxWidth: { xs: "45vw", sm: "100%" } }}
            onClick={() => navigate("/")}
          >
            <img
              src="/frontend/assets/logos/logo.png"
              alt="Logo"
              style={{ height: 90, maxWidth: "100%", objectFit: "contain" }}
            />
          </Box>

          {/* CENTER NAV */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              gap: 3,
              alignItems: "center",
            }}
          >
            {navItems.map((item, i) => (
              <Box
                key={i}
                onMouseEnter={(e) => handleHoverOpen(e, item)}
                onMouseLeave={handleHoverClose}
                sx={{ display: "flex", alignItems: "center", height: "100%" }}
              >
                <Typography
                  onClick={() => {
                    if (item.slug) {
                      navigate(`/category/${item.slug}`);
                    }
                  }}
                  sx={{
                    fontSize: 19,
                    fontWeight: 550,
                    cursor: "pointer",
                    "&:hover": { color: "red" },
                    color: hoveredNavItem?.slug === item.slug ? "red" : "inherit",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}

            {/* POPUP BUTTON */}
            <Typography
              sx={{ fontSize: 22, cursor: "pointer" }}
              onClick={handleOpenPopup}
            >
              ⋮
            </Typography>
          </Box>

          {/* RIGHT ICONS */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              gap: 2.5,
              alignItems: "center",
              fontSize: 22,
            }}
          >
            {/* Social + Notifications */}
            {[
              { icon: <FacebookIcon fontSize="small" />, key: "facebook" },
              { icon: <TwitterIcon fontSize="small" />, key: "twitter" },
              { icon: <InstagramIcon fontSize="small" />, key: "instagram" },
              { icon: <WhatsAppIcon fontSize="small" />, key: "whatsapp" },
              { icon: <YouTubeIcon fontSize="small" />, key: "youtube" },
            ].map((item) => (
              <IconButton
                key={item.key}
                sx={{
                  backgroundColor: "#000",
                  color: "#fff",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    backgroundColor: "#000",
                    color: "#ff184e",
                  },
                }}
              >
                {item.icon}
              </IconButton>
            ))}
            <Badge badgeContent={readNotifications ? 0 : notifications?.filter(n => n.id > lastSeenId).length} color="error" sx={{ cursor: "pointer" }} onClick={handleOpenNotify}>
              <BsBell style={{ fontSize: "22px" }} />
            </Badge>
            {/* <Typography sx={{ fontSize: 17, fontWeight: 600 }}>Aa</Typography> */}

            {/* 🔥 DARK/LIGHT MODE TOGGLE */}
            <Box onClick={colorMode.toggleColorMode} sx={{ cursor: "pointer" }}>
              {theme.palette.mode === "light" ? (
                <CiDark style={{ fontSize: 26 }} /> // Light → show Moon
              ) : (
                <CiBrightnessUp style={{ fontSize: 26 }} /> // Dark → show Sun
              )}
            </Box>
          </Box>

          {/* MOBILE MENU & SEARCH BUTTONS */}
          <ClickAwayListener
            onClickAway={() => {
              if (mobileSearchOpen) {
                setMobileSearchOpen(false);
                setSearchQuery("");
                setSuggestion([]);
              }
            }}
          >
            <Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", position: "relative" }}>

              {/* Animated Expanding Search Bar */}
              <Box
                sx={{
                  position: "absolute",
                  right: "100%",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  width: mobileSearchOpen ? { xs: "200px", sm: "240px" } : "0px",
                  maxWidth: { xs: "42vw", sm: "240px" },
                  opacity: mobileSearchOpen ? 1 : 0,
                  visibility: mobileSearchOpen ? "visible" : "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  mr: mobileSearchOpen ? 1 : 0,
                  backgroundColor: theme.palette.mode === "light" ? "#f0f0f0" : "#222",
                  borderRadius: "20px",
                  height: "36px",
                  zIndex: 10,
                }}
              >
                <TextField
                  inputRef={mobileInputRef}
                  fullWidth
                  placeholder="Search news..."
                  variant="standard"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      navigate(`/search?q=${searchQuery}`);
                      setMobileSearchOpen(false);
                      setSearchQuery("");
                      setSuggestion([]);
                    }
                  }}
                  InputProps={{
                    disableUnderline: true,
                    sx: { px: 2, fontSize: "16px", color: "text.primary" }
                  }}
                />
              </Box>

              {/* Search / Close Toggle Button */}
              <IconButton
                onClick={() => {
                  if (mobileSearchOpen) {
                    setMobileSearchOpen(false);
                    setSearchQuery("");
                    setSuggestion([]);
                  } else {
                    setMobileSearchOpen(true);
                    setTimeout(() => mobileInputRef.current?.focus(), 100);
                  }
                }}
                sx={{ fontSize: 24, color: "text.primary", mr: mobileSearchOpen ? 0 : 0.5 }}
              >
                {mobileSearchOpen ? <MdClose /> : <FiSearch />}
              </IconButton>

              {/* Hide Hamburger when Search is active to save space */}
              {!mobileSearchOpen && (
                <IconButton
                  sx={{ fontSize: 32, color: "text.primary" }}
                  onClick={() => setOpen(true)}
                >
                  <HiMenuAlt3 />
                </IconButton>
              )}

              {/* Mobile Real-time Search Suggestions Dropdown */}
              {mobileSearchOpen && suggestion.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "140%",
                    right: 0,
                    width: { xs: "280px", sm: "320px" },
                    maxWidth: "90vw",
                    boxSizing: "border-box",
                    backgroundColor: "background.paper",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                    borderRadius: 2,
                    maxHeight: 350,
                    overflowY: "auto",
                    overflowX: "hidden",
                    zIndex: 1200,
                    p: 1,
                    border: theme.palette.mode === "dark" ? "1px solid #333" : "none",
                  }}
                >
                  {suggestion.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => {
                        navigate(`/${item.slug}`);
                        setMobileSearchOpen(false);
                        setSearchQuery("");
                        setSuggestion([]);
                      }}
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 1,
                        borderRadius: 1,
                        mb: 0.5,
                        alignItems: "center",
                        transition: "0.2s",
                        "&:hover": {
                          backgroundColor: "rgba(255, 24, 78, 0.1)",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Avatar
                        src={item.image ? `/${item.image}` : noImage}
                        variant="rounded"
                        sx={{ width: 45, height: 45 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {item.title}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        </Toolbar>
        {/* 🔥 TOP HEADLINES TICKER 🔥 */}
        <Box
          sx={{
            width: "100%",
            background: "#ff184e",
            color: "#fff",
            overflow: "hidden",
            py: { md: 1.5, xs: 1 },
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              whiteSpace: "nowrap",
              width: "max-content",
              animation: `${marquee} 40s linear infinite`,
              "&:hover": {
                animationPlayState: "paused",
              },
            }}
          >
            {[...(news || []), ...(news || [])].map((item, index) => (
              <Typography
                key={`${item.id}-${index}`}
                onClick={() => navigate(`/${item.slug}`)}
                sx={{
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: 600,
                  px: 3,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
              • {item.title}
              </Typography>
            ))}
          </Box>
        </Box>
      </AppBar>
      {/* MOBILE DRAWER */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ display: "flex",justifyContent: "center", alignItems: "center" }}>
        <img
              src="/frontend/assets/logos/logo.png"
              alt="Logo"
              style={{ height: 100, width: 200,}}
            />
            </Box>
            <Divider sx={{ my: 2 }} />
        <Box sx={{ width: 260, p: 2, }}>
          <List>
            {navItems.map((item) => (
              <React.Fragment key={item.slug}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (item.cities) {
                        handleMobileMenuToggle(item.slug);
                      } else {
                        if (item.slug) {
                          navigate(`/category/${item.slug}`);
                        }
                        setOpen(false);
                      }
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 18, fontWeight: 550, '&:hover': { color: 'red' } }}
                    />
                    {item.cities ? (
                      mobileMenuOpen[item.slug] ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />
                    ) : null}
                  </ListItemButton>
                </ListItem>
                {item.cities && (
                  <Collapse in={mobileMenuOpen[item.slug]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.cities.map((city) => (
                        <ListItemButton
                          key={city}
                          sx={{ pl: 4 }}
                          onClick={() => {
                            navigate(`/category/${item.slug}?city=${city}`);
                            setOpen(false);
                          }}
                        >
                          <ListItemText primary={city} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />
        </Box>
        <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: "center",
              gap: 1.5,
              mt: { xs: 3, md: 0 },
            }}
          >
          {[
              { icon: <FacebookIcon fontSize="small" />, key: "facebook" },
              { icon: <TwitterIcon fontSize="small" />, key: "twitter" },
              { icon: <InstagramIcon fontSize="small" />, key: "instagram" },
              { icon: <WhatsAppIcon fontSize="small" />, key: "whatsapp" },
              { icon: <YouTubeIcon fontSize="small" />, key: "youtube" },
            ].map((item) => (
              <IconButton
                key={item.key}
                sx={{
                  backgroundColor: "#000",
                  color: "#fff",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    backgroundColor: "#000",
                    color: "#ff184e",
                  },
                }}
              >
                {item.icon}
              </IconButton>
            ))}
            </Grid>
      </Drawer>
      {/* 🔔 Notification Popover */}
      <Popover
        open={openNotify}
        anchorEl={notifyAnchor}
        onClose={handleCloseNotify}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 2,
            width: { xs: "98%", sm: "100%", md: 300 },
            maxWidth: { xs: "95vw", md: 300 },
            maxHeight: 400,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Typography
          sx={{ fontSize: 18, fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          Latest Notifications
        </Typography>

        <Divider sx={{ mb: 1 }} />

        <Box sx={{ maxHeight: 330, overflowY: "auto", overflowX: "hidden", pr: 1, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }} onScroll={handleNotificationScroll}>
          {notifications?.length === 0 && !notificationLoading ? (
            <Typography sx={{ p: 2, textAlign: "center", fontSize: 14 }}>No new notifications</Typography>
          ) : (
            notifications?.map((item, index) => {
            const isNew = item.id > lastSeenId;
            return (
            <Box
              key={`${item.id}-${index}`}
              onClick={() => {
                navigate(`/${item.slug}`);
                handleCloseNotify();
              }}
              sx={{
                display: "flex",
                gap: 2,
                p: 1,
                borderRadius: 1,
                mb: 1,
                position: "relative",
                backgroundColor: isNew ? "rgba(255, 24, 78, 0.08)" : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 24, 78, 0.1)",
                  cursor: "pointer",
                },
              }}
            >
              <Avatar
                src={item.image ? `/${item.image}` : noImage}
                variant="rounded"
                sx={{ width: 50, height: 50 }}
              />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}>
                  {item.title?.length > 60 ? item.title.slice(0, 60) + "..." : item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                {item.time_ago || dayjs(item.published_at).fromNow()}
                </Typography>
              </Box>
              {isNew && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#ff184e",
                    alignSelf: "center",
                  }}
                />
              )}
            </Box>
          )}))}
          {notificationLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      </Popover>

      {/* 🔥 SEARCH POPUP UNDER BUTTON */}
      <Popover
        open={openPopup}
        anchorEl={anchorEl}
        onClose={handleClosePopup}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: { xs: "100%", md: 300 },
            maxWidth: { xs: "90vw", md: 300 },
            borderRadius: 2,
            overflowX: "hidden",
            boxSizing: "border-box",
          },
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              autoFocus
              fullWidth
              placeholder="Search news..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate(`/search?q=${searchQuery}`);
                  handleClosePopup();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch size={18} />
                  </InputAdornment>
                ),
                sx: {
                  height: 32,
                  paddingRight: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#999",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#333",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ccc",
                  },
                },
              }}
              inputProps={{
                sx: {
                  padding: "8px 0",
                  fontSize: 16,
                },
              }}
            />
            <Button
              size="small"
              variant="outlined"
              sx={{ border: "1px solid #ccc", color: "#000" }}
              onClick={() => {
                navigate(`/search?q=${searchQuery}`);
                handleClosePopup();
              }}
            >
              Submit
            </Button>
          </Box>
          {suggestion.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 300, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              {suggestion.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => {
                    navigate(`/${item.slug}`);
                    handleClosePopup();
                  }}
                  sx={{
                    display: "flex",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    mb: 1,
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: "rgba(255, 24, 78, 0.1)",
                      cursor: "pointer",
                    },
                  }}
                >
                  <Avatar
                    src={item.image ? `/${item.image}` : noImage}
                    variant="rounded"
                    sx={{ width: 50, height: 50 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.title}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      {item.category?.name && (
                        <Typography variant="caption" sx={{ color: "#ff184e", fontWeight: "bold", fontSize: "10px" }}>
                          {item.category.name}
                        </Typography>
                      )}
                      {item.author?.name && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "10px" }}>
                          {item.author.name} •
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>
                    {item.time_ago || dayjs(item.published_at).fromNow()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>

      {/* 🔥 HOVER MENU FOR CITIES */}
      <Menu
        anchorEl={hoverMenuAnchor}
        open={Boolean(hoverMenuAnchor)}
        onClose={handleHoverClose}
        MenuListProps={{
          onMouseEnter: handleMenuEnter,
          onMouseLeave: handleHoverClose,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiPaper-root": {
            pointerEvents: "auto",
            minWidth: 100,
            maxHeight: 220,
            overflowY: "auto",
            // Hide scrollbar for IE, Edge and Firefox
            msOverflowStyle: "none",  // IE and Edge
            scrollbarWidth: "none",  // Firefox
            "&::-webkit-scrollbar": {
              display: "none", // Hide scrollbar for Chrome, Safari and Opera
            },
          },
        }}
        disableRestoreFocus
        hideBackdrop
      >
        {hoveredNavItem?.cities?.map((city) => (
          <MenuItem
            key={city}
            onClick={() => handleCityClick(city)}
            sx={{ fontSize: "14px", fontWeight: 500 }}
          >
            {city}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
